import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { commonToast } from '../../../helper/Toast';
// import { BiImport } from 'react-icons/bi';
import { FaPlus, FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import { IoMdSearch } from 'react-icons/io';
import Select from 'react-select';
import TailwindDataTable from '../../../components/TailwindDataTable';
import PageSizeSelectBox from '../../../components/PageSizeSelectBox';
import ReactPaginate from 'react-paginate';
import { getSelectedSchoolId } from '../../../helper/commonFunctions';
import AddAdminUserModel from '../../../components/model/AddAdminUserModel';
import EditTeacherModel from '../../../components/model/EditTeacherModel';
import ViewTeacherModel from '../../../components/model/ViewTeacherModel';
import DeleteConfirmationModel from '../../../components/model/DeleteConfirmationModel';
import { deleteTeacher, getApiError, getTeacherId, parseListResponse, searchTeachers } from '../../../helper/adminUsersApi';

export default function TeacherList() {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [teachers, setTeachers] = useState([]);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editingTeacher, setEditingTeacher] = useState(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [deletingTeacher, setDeletingTeacher] = useState(null);
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [viewingTeacher, setViewingTeacher] = useState(null);
    const schoolId = getSelectedSchoolId();

    const fetchTeachers = async () => {
        try {
            setLoading(true);
            const response = await searchTeachers({
                offset: currentPage * limit,
                limit,
                schoolId,
            });
            const list = parseListResponse(response);
            setTeachers(list);
            setTotalPages(list.length < limit ? currentPage + 1 : currentPage + 2);
        } catch (error) {
            console.error('Error fetching teachers:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeachers();
    }, [currentPage, limit]);

    const columns = [
        {
            key: 'username',
            label: t('teacher_management.name'),
            formatter: (_, row) => {
                if (!row) return null;
                const initials = row.firstName ? row.firstName[0] : '';
                return (
                    <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-500 text-sm font-medium">
                                    {initials}
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{`${row.firstName || ''} ${row.lastName || ''}`}</div>
                            <div className="text-sm text-gray-500">{row.email || ''}</div>
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'countryCode',
            label: t('teacher_management.country'),
            formatter: (cell) => cell || '-'
        },
        {
            key: 'userRoles',
            label: t('teacher_management.roles'),
            formatter: (cell) => {
                if (!cell?.length) return null;
                return (
                    <div className="flex flex-wrap gap-1">
                        {cell.map((role, index) => (
                            <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {role.replace('zubrus_', '')}
                            </span>
                        ))}
                    </div>
                );
            }
        },
        {
            key: 'status',
            label: t('teacher_management.status'),
            formatter: (cell) => {
                if (!cell) return null;
                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cell === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {cell}
                    </span>
                );
            }
        },
        {
            key: 'creationDate',
            label: t('teacher_management.registration_date'),
            formatter: (cell) => cell ? new Date(cell).toLocaleDateString() : '-'
        },
        {
            key: 'actions',
            label: t('teacher_management.actions'),
            formatter: (_, row) => {
                if (!row) return null;
                return (
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => handleViewTeacher(row)}
                            className="text-green-600 hover:text-green-800 transition-colors duration-200"
                            title={t('view')}
                        >
                            <FaEye size={18} />
                        </button>
                        <button
                            onClick={() => handleEditTeacher(row)}
                            className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                            title={t('edit')}
                        >
                            <FaEdit size={18} />
                        </button>
                        <button
                            onClick={() => handleDeleteTeacher(row)}
                            className="text-red-600 hover:text-red-800 transition-colors duration-200"
                            title={t('remove')}
                        >
                            <FaTrash size={18} />
                        </button>
                    </div>
                );
            }
        }
    ];

    const handleEditTeacher = (teacher) => {
        if (!teacher) return;
        setEditingTeacher(teacher);
        setIsEditOpen(true);
    };

    const handleDeleteTeacher = (teacher) => {
        if (!teacher) return;
        setDeletingTeacher(teacher);
        setIsDeleteOpen(true);
    };

    const confirmDeleteTeacher = async () => {
        const teacherId = getTeacherId(deletingTeacher);
        if (!teacherId) {
            commonToast(t('teacher_management.teacher_not_found'), 'error');
            throw new Error('Missing teacherId');
        }
        try {
            await deleteTeacher(teacherId);
            commonToast(t('teacher_management.delete_success'), 'success');
            await fetchTeachers();
        } catch (error) {
            commonToast(getApiError(error, t('teacher_management.delete_failed')), 'error');
            throw error;
        }
    };

    const handlePageChange = (selectedItem) => {
        setCurrentPage(selectedItem.selected);
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(0); // Reset to first page when searching
    };

    const handleStatusFilter = (selectedOption) => {
        setFilterStatus(selectedOption.value);
        setCurrentPage(0); // Reset to first page when filtering
    };

    const handleViewTeacher = (teacher) => {
        if (!teacher) return;
        setViewingTeacher(teacher);
        setIsViewOpen(true);
    };

    const filteredTeachers = teachers.filter(teacher => {
        const fullName = `${teacher.firstName || ''} ${teacher.lastName || ''}`.toLowerCase();
        const matchesSearch = fullName.includes(searchQuery.toLowerCase()) ||
            (teacher.email || '').toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = filterStatus === 'all' || teacher.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const statusOptions = [
        { value: 'all', label: t('all') },
        { value: 'ACTIVE', label: t('teacher_management.active') },
        { value: 'INACTIVE', label: t('teacher_management.inactive') }
    ];

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow p-6">
                {/* Header */}
                <div className="flex flex-wrap justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">{t('teacher_management.title')}</h1>
                    <div className="flex gap-4 md:mt-4 sm:flex-col">
                        {/* <button className="primary-btn">
                            <BiImport size={20} />
                            {t('teacher_management.import_csv')}
                        </button> */}
                        <button
                            type="button"
                            onClick={() => setIsAddOpen(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                            <FaPlus size={20} />
                            {t('teacher_management.add_teacher')}
                        </button>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={t('teacher_management.search_placeholder')}
                                className="w-1/2 md:w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={searchQuery}
                                onChange={handleSearch}
                            />
                            <IoMdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        </div>
                    </div>
                    <div className="min-w-[200px]">
                        <Select
                            options={statusOptions}
                            placeholder={t('teacher_management.filter_by_status')}
                            onChange={handleStatusFilter}
                            defaultValue={statusOptions[0]}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                </div>

                {/* Data Table */}
                <>
                    <TailwindDataTable
                        columns={columns}
                        data={filteredTeachers}
                        thClasses='text-white'
                        loading={loading}
                    />

                    {/* Pagination Controls */}
                    <div className="flex justify-between items-center mt-4">
                        <div className='xs:mb-5'>
                            <PageSizeSelectBox selected={limit} setSelected={setLimit} setPage={setCurrentPage} />
                        </div>
                        <div className='flex gap-4'>
                            <ReactPaginate
                                breakLabel="..."
                                nextLabel=">"
                                onPageChange={handlePageChange}
                                pageRangeDisplayed={5}
                                pageCount={totalPages}
                                previousLabel="<"
                                renderOnZeroPageCount={null}
                                containerClassName="flex gap-2"
                                pageClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                activeClassName="bg-blue-500 text-white"
                                previousClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                nextClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                disabledClassName="opacity-50 cursor-not-allowed"
                            />
                        </div>
                    </div>
                </>
                <AddAdminUserModel
                    open={isAddOpen}
                    setOpen={setIsAddOpen}
                    onCreated={fetchTeachers}
                    role="teacher"
                    schoolId={schoolId}
                />
                <EditTeacherModel
                    open={isEditOpen}
                    setOpen={setIsEditOpen}
                    teacher={editingTeacher}
                    schoolId={schoolId}
                    onUpdated={fetchTeachers}
                />
                <ViewTeacherModel
                    open={isViewOpen}
                    setOpen={setIsViewOpen}
                    teacher={viewingTeacher}
                />
                <DeleteConfirmationModel
                    open={isDeleteOpen}
                    setOpen={setIsDeleteOpen}
                    onConfirm={confirmDeleteTeacher}
                    title="teacher_management.delete_title"
                    message="teacher_management.delete_confirm"
                    confirmText="Delete"
                    cancelText="Cancel"
                />
            </div>
        </div>
    );
}
